import { useState } from "react";

/**
 * Calendario para elegir un rango de días dentro del desplegable de período.
 *
 * La semana arranca en lunes, que es como se lee un calendario acá. Los días
 * futuros quedan deshabilitados: no hay depósitos por llegar, y ofrecerlos
 * invita a elegir un rango que siempre devuelve vacío.
 */
const DIAS = ["L", "M", "M", "J", "V", "S", "D"];
const MESES = ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio",
               "agosto", "septiembre", "octubre", "noviembre", "diciembre"];

/** YYYY-MM-DD en hora local. `toISOString()` corre la fecha al pasar por UTC. */
export function iso(d: Date) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${
    String(d.getDate()).padStart(2, "0")}`;
}

export function fechaCorta(s: string) {
  const [a, m, d] = s.split("-").map(Number);
  return `${d} ${MESES[m - 1]?.slice(0, 3)}${a !== new Date().getFullYear() ? ` ${a}` : ""}`;
}

export function CalendarioRango({ onAplicar }: {
  onAplicar: (desde: string, hasta: string) => void;
}) {
  const hoy = new Date();
  const [mes, setMes] = useState(new Date(hoy.getFullYear(), hoy.getMonth(), 1));
  const [desde, setDesde] = useState("");
  const [hasta, setHasta] = useState("");

  const limiteHoy = iso(hoy);
  const primero = new Date(mes.getFullYear(), mes.getMonth(), 1);
  const ultimo = new Date(mes.getFullYear(), mes.getMonth() + 1, 0);
  // getDay() da 0=domingo; acá la semana arranca en lunes.
  const relleno = (primero.getDay() + 6) % 7;

  const celdas: (string | null)[] = [
    ...Array(relleno).fill(null),
    ...Array.from({ length: ultimo.getDate() },
                  (_, i) => iso(new Date(mes.getFullYear(), mes.getMonth(), i + 1))),
  ];

  function elegir(d: string) {
    // Un solo extremo puesto y el nuevo es anterior: se reinicia desde ahí en
    // vez de armar un rango invertido.
    if (!desde || (desde && hasta) || d < desde) { setDesde(d); setHasta(""); }
    else setHasta(d);
  }

  const completo = !!(desde && hasta);
  const etiqueta = completo ? `${fechaCorta(desde)} – ${fechaCorta(hasta)}`
    : desde ? `${fechaCorta(desde)} – elegí el final`
    : "Elegí el primer día";

  return (
    <div className="cal">
      <div className="cal-nav">
        <span className="cal-titulo">Otro · rango</span>
        <button onClick={() => setMes(new Date(mes.getFullYear(), mes.getMonth() - 1, 1))}
                aria-label="Mes anterior">‹</button>
        <span className="cal-mes">{MESES[mes.getMonth()]} {mes.getFullYear()}</span>
        <button disabled={mes >= new Date(hoy.getFullYear(), hoy.getMonth(), 1)}
                onClick={() => setMes(new Date(mes.getFullYear(), mes.getMonth() + 1, 1))}
                aria-label="Mes siguiente">›</button>
      </div>

      <div className="cal-grid">
        {DIAS.map((d, i) => <div key={i} className="cal-dia">{d}</div>)}
        {celdas.map((d, i) => {
          if (!d) return <span key={i} />;
          const futuro = d > limiteHoy;
          const esInicio = d === desde;
          const esFin = d === hasta;
          const dentro = completo && d > desde && d < hasta;
          const clase = esInicio && esFin ? " solo"
            : esInicio ? " inicio" : esFin ? " fin" : dentro ? " dentro" : "";
          return (
            <button key={i} className={"cal-celda" + clase}
                    disabled={futuro} onClick={() => elegir(d)}>
              {Number(d.slice(-2))}
            </button>
          );
        })}
      </div>

      <div className="cal-pie">
        <span className={"mono cal-rango" + (completo ? " listo" : "")}>{etiqueta}</span>
        <button className="btn ghost cal-chico"
                onClick={() => { setDesde(""); setHasta(""); }}>
          Limpiar
        </button>
        <button className="btn primary cal-chico" disabled={!completo}
                onClick={() => completo && onAplicar(desde, hasta)}>
          Aplicar
        </button>
      </div>
    </div>
  );
}
